import React, { useState, useMemo } from 'react';
import { useData } from '../../context/DataContext';
import { Payment } from '../../types';
import { 
  ClipboardListIcon, 
  XIcon, 
  ShieldCheckIcon,
  RestaurantIcon,
  ClockIcon
} from '../../components/icons';

const SuperAdminPayments: React.FC = () => {
  const { 
    allPayments, 
    validatePayment,
    rejectPayment 
  } = useData();
  
  const [selectedProof, setSelectedProof] = useState<string | null>(null);
  const [paidAmountInputs, setPaidAmountInputs] = useState<Record<string, string>>({});

  const handlePaidAmountChange = (paymentId: string, value: string) => {
    setPaidAmountInputs(prev => ({ ...prev, [paymentId]: value }));
  };

  const pendingPayments = useMemo(() => {
    return allPayments.filter(p => p.status === 'pending');
  }, [allPayments]);

  const validatedPayments = useMemo(() => {
    return allPayments.filter(p => p.status === 'validated').slice(0, 20); // Last 20
  }, [allPayments]);

  const getProof = (p: any) => p.proofUrl || p.proofImage || p.proof_url || p.proof || null;

  const ProofButton = ({ payment }: { payment: any }) => {
    const proof = getProof(payment);
    if (!proof) return (
      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
        <span className="text-[8px] text-gray-400 font-bold uppercase text-center leading-tight px-1">Aucune preuve</span>
      </div>
    );
    if (proof === 'auto_commission') return (
      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center border border-gray-100">
        <ClipboardListIcon className="w-6 h-6 text-gray-400" />
      </div>
    );
    return (
      <button
        onClick={() => setSelectedProof(proof)}
        className="relative group w-14 h-14 rounded-2xl overflow-hidden border-2 border-blue-100 shadow-sm hover:scale-105 transition-transform"
      >
        <img src={proof} alt="Preuve" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-blue-600/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <ShieldCheckIcon className="w-5 h-5 text-white" />
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <h1 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
        <ClipboardListIcon className="w-8 h-8 text-purple-600" />
        Validation des Paiements
      </h1>
      
      {/* Pending Section */}
      <h2 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wide">
        <ClockIcon className="w-5 h-5 text-orange-500" />
        En attente
      </h2>
      <div className="space-y-4 mb-10">
        {pendingPayments.length === 0 ? (
          <div className="bg-white p-12 rounded-3xl text-center border-2 border-dashed border-gray-100 italic text-gray-400">
             Aucun paiement en attente.
          </div>
        ) : (
          pendingPayments.map((payment) => (
            <div 
              key={payment.id} 
              className="bg-white p-5 rounded-3xl shadow-sm border border-gray-50 flex flex-col gap-4"
            >
              <div className="flex items-center gap-4">
                <ProofButton payment={payment} />
                <div className="flex-1 text-left">
                    <h3 className="font-black text-gray-900">{payment.restaurantName}</h3>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-tight">
                      {payment.planType} • {payment.period || (payment.planType === 'annuel' ? '1 an' : '30 jours')}
                    </p>
                    <p className="text-xl font-black text-purple-600 mt-1">{payment.amount} DA</p>
                    {/* Explicit proof link */}
                    {(() => {
                      const proof = getProof(payment);
                      if (!proof || proof === 'auto_commission') return null;
                      return (
                        <button
                          onClick={() => setSelectedProof(proof)}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-black text-blue-600 uppercase underline underline-offset-2"
                        >
                          <ShieldCheckIcon className="w-3 h-3" />
                          Voir la preuve de paiement
                        </button>
                      );
                    })()}
                    {payment.description && (
                      <p className="text-[10px] font-bold text-gray-500 mt-2 bg-gray-50 p-2 rounded-lg border border-gray-100 italic">
                        "{payment.description}"
                      </p>
                    )}
                </div>
              </div>

              {/* Paid Amount Input for Commission */}
              {payment.planType === 'commission' && (
                <div className="bg-gray-50 p-4 rounded-2xl flex flex-col gap-2">
                  <div className="flex justify-between items-center px-1">
                    <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Montant Attendu</span>
                    <span className="text-sm font-black text-purple-600">{payment.amount} DA</span>
                  </div>
                  <div className="relative group">
                    <input 
                      type="number"
                      placeholder="Montant réellement payé..."
                      value={paidAmountInputs[payment.id] || ''}
                      onChange={(e) => handlePaidAmountChange(payment.id, e.target.value)}
                      className="w-full bg-white border-2 border-gray-100 rounded-xl py-3 px-4 text-sm font-bold focus:border-purple-600 focus:ring-0 transition-all outline-none"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-300 uppercase">DA</div>
                  </div>
                  <div className="flex justify-between items-center px-1 mt-1">
                    {(() => {
                      const paid = paidAmountInputs[payment.id] !== '' ? Number(paidAmountInputs[payment.id]) : payment.amount;
                      const diff = (payment.amount || 0) - (paid || 0);
                      if (diff > 0) {
                        return <span className="text-[9px] text-red-600 font-black uppercase italic">Report Dette: +{diff.toLocaleString()} DA</span>;
                      } else if (diff < 0) {
                        return <span className="text-[9px] text-green-600 font-black uppercase italic">Crédit Restaurant: {Math.abs(diff).toLocaleString()} DA</span>;
                      }
                      return <span className="text-[9px] text-gray-400 font-black uppercase italic">Paiement Complet</span>;
                    })()}
                  </div>
                  <p className="text-[10px] text-gray-400 italic px-1 leading-tight mt-1">
                    Le montant payé sera déduit. La différence sera ajoutée ou déduite de la prochaine facture.
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                 <button 
                  onClick={() => {
                    const paidValue = paidAmountInputs[payment.id];
                    const paid = paidValue !== undefined && paidValue !== '' ? Number(paidValue) : undefined;
                    
                    const msg = payment.planType === 'commission' && paid !== undefined && paid !== payment.amount
                      ? `Valider avec un paiement de ${paid} DA ? (Dette de ${payment.amount - (paid || 0)} DA sera reportée)`
                      : 'Valider cet abonnement ?';
                    
                    if (confirm(msg)) {
                        validatePayment(payment.id, payment.restaurantId, paid).then(() => {
                           // Clear input for this specific payment after success for better reactivity
                           handlePaidAmountChange(payment.id, '');
                        });
                    }
                  }}
                  className="flex-1 bg-green-600 text-white py-3 rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-green-700 transition-colors shadow-lg shadow-green-100"
                 >
                   Valider
                 </button>
                 <button 
                  onClick={() => { if(confirm('Rejeter ce paiement?')) rejectPayment(payment.id) }}
                  className="flex-1 bg-red-50 text-red-600 py-3 rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-red-100 transition-colors"
                 >
                   Rejeter
                 </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* History Section */}
      <h2 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wide">
        <ShieldCheckIcon className="w-5 h-5 text-green-500" />
        Historique Récent
      </h2>
      <div className="space-y-3">
        {validatedPayments.map((payment) => {
          const proof = getProof(payment);
          return (
            <div key={payment.id} className="bg-white p-4 rounded-2xl flex items-center gap-4 shadow-sm border border-gray-50">
              {/* Proof thumbnail */}
              {proof && proof !== 'auto_commission' ? (
                <button
                  onClick={() => setSelectedProof(proof)}
                  className="relative group w-12 h-12 rounded-xl overflow-hidden border-2 border-blue-100 shadow-sm shrink-0 hover:scale-105 transition-transform"
                >
                  <img src={proof} alt="Preuve" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-blue-600/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ShieldCheckIcon className="w-4 h-4 text-white" />
                  </div>
                </button>
              ) : (
                <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                  <ClipboardListIcon className="w-5 h-5 text-gray-300" />
                </div>
              )}

              <div className="flex-1 text-left min-w-0">
                <p className="font-bold text-gray-900 text-sm truncate">{payment.restaurantName}</p>
                <p className="text-[10px] text-gray-400 font-medium">{payment.amount} DA • {payment.planType}</p>
                {payment.description && (
                  <p className="text-[9px] text-gray-400 italic mt-0.5 truncate">
                    {payment.description}
                  </p>
                )}
                {proof && proof !== 'auto_commission' && (
                  <button
                    onClick={() => setSelectedProof(proof)}
                    className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-black text-blue-500 uppercase"
                  >
                    <ShieldCheckIcon className="w-2.5 h-2.5" />
                    Voir preuve
                  </button>
                )}
              </div>
              <span className="text-[9px] bg-green-50 text-green-600 px-2 py-1 rounded-full font-black uppercase shrink-0">Validé</span>
            </div>
          );
        })}
      </div>

      {/* Image Preview Modal */}
      {selectedProof && (
        <div className="fixed inset-0 z-[110] bg-black/90 flex flex-col items-center justify-center p-4" onClick={() => setSelectedProof(null)}>
           <button className="absolute top-8 right-8 bg-white/10 p-3 rounded-full text-white backdrop-blur-md">
              <XIcon className="w-8 h-8" />
           </button>
           <img src={selectedProof} alt="Full Proof" className="max-w-full max-h-[80vh] rounded-2xl shadow-2xl" />
           <p className="text-white font-black mt-6 text-lg tracking-widest uppercase italic">Preuve de Paiement</p>
           <p className="text-white/50 mt-2 font-bold text-xs">Toucher pour fermer</p>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPayments;
